"use client";
import posthog from "posthog-js";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";
import {
  buildTelemetryContext,
  isClientTelemetryDeliveryEnabled,
} from "@/lib/telemetry/environment";
import { buildRuntimeContext } from "@/lib/telemetry/runtime";
import { version } from "../../../package.json";
import {
  type Acquisition,
  AD_MATCHING_NOTICE_VERSION,
  type AdConsent,
  acquisitionProperties,
  campaignParameters,
  mergeAcquisition,
  sanitizeAcquisition,
} from "./shared";

const KEY = "hiive:acquisition:v1";
let memory: Acquisition | undefined;
let syncChain: Promise<boolean> = Promise.resolve(false);
let observedUrl = "";
export const ACQUISITION_CHANGED = "hiive:acquisition-changed";
export const DEFERRED_DEEP_LINK = "hiive:deferred-deep-link";
let pendingDeferredLink: string | null = null;
/** Hands a validated in-app path to the deep-link handler, mounted or not. */
export function queueDeferredDeepLink(path: string) {
  pendingDeferredLink = path;
  window.dispatchEvent(new Event(DEFERRED_DEEP_LINK));
}
export function takeDeferredDeepLink(): string | null {
  const path = pendingDeferredLink;
  pendingDeferredLink = null;
  return path;
}
export function readClientAcquisition(): Acquisition {
  if (typeof window === "undefined") return sanitizeAcquisition(null);
  if (memory) return memory;
  try {
    memory = sanitizeAcquisition(
      JSON.parse(localStorage.getItem(KEY) ?? "null"),
    );
  } catch {
    memory = sanitizeAcquisition(null);
  }
  return memory;
}
export function captureAcquisitionEvent(
  event: string,
  properties: Record<string, unknown> = {},
) {
  if (!isClientTelemetryDeliveryEnabled()) return;
  try {
    return Boolean(
      posthog.capture(event, {
        ...buildTelemetryContext(version),
        ...buildRuntimeContext(),
        ...acquisitionProperties(readClientAcquisition()),
        ...properties,
      }),
    );
  } catch {
    /* Telemetry never interrupts a navigation. */
  }
}
export function saveClientAcquisition(context: Acquisition, sync = true) {
  const next = sanitizeAcquisition(context);
  if (JSON.stringify(memory) === JSON.stringify(next)) return;
  memory = next;
  try {
    localStorage.setItem(KEY, JSON.stringify(memory));
  } catch {
    /* A private WebView can still use the in-memory context. */
  }
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(ACQUISITION_CHANGED));
  if (sync) void syncAcquisition();
}
export function syncAcquisition(): Promise<boolean> {
  const snapshot = readClientAcquisition();
  syncChain = syncChain
    .catch(() => false)
    .then(async () => {
      try {
        const response = await fetch("/api/acquisition", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(snapshot),
          keepalive: true,
        });
        if (!response.ok) return false;
        const body = await response.json();
        // Ignore responses belonging to an identity that has since changed.
        if (snapshot.owner_user_id !== readClientAcquisition().owner_user_id)
          return false;
        saveClientAcquisition(
          mergeAcquisition(
            readClientAcquisition(),
            sanitizeAcquisition(body.context),
          ),
          false,
        );
        return true;
      } catch {
        /* Retry on the next route or identity boundary. */
        return false;
      }
    });
  return syncChain;
}
export function observeAcquisitionUrl(rawUrl: string, capture = true) {
  try {
    const url = new URL(rawUrl, window.location.origin);
    // Untagged visits still need a surface for consented server conversions.
    // Campaign dimensions remain empty unless the landing actually has them.
    const previous = readClientAcquisition();
    const surface = buildRuntimeContext().app_surface;
    if (previous.app_surface !== surface)
      saveClientAcquisition({ ...previous, app_surface: surface });
    const campaign = campaignParameters(url);
    const signature = JSON.stringify(campaign);
    if (!Object.keys(campaign).length || signature === observedUrl) return;
    observedUrl = signature;
    const touch = {
      id: crypto.randomUUID(),
      at: Date.now(),
      source: "url" as const,
      status: "observed" as const,
      campaign,
    };
    const current = readClientAcquisition();
    saveClientAcquisition({
      ...current,
      first: current.first ?? touch,
      last: touch,
      app_surface: buildRuntimeContext().app_surface,
    });
    if (capture)
      captureAcquisitionEvent(ANALYTICS_EVENTS.ACQUISITION_LANDING_VIEWED, {
        landing_path: url.pathname,
      });
  } catch {
    /* Malformed links cannot break launch. */
  }
}
export function setAdMeasurementConsent(consent: AdConsent) {
  saveClientAcquisition({
    ...readClientAcquisition(),
    consent,
    consent_at: Date.now(),
    consent_notice_version: AD_MATCHING_NOTICE_VERSION,
  });
  captureAcquisitionEvent(ANALYTICS_EVENTS.AD_MEASUREMENT_CONSENT_CHANGED, {
    consent,
  });
}
export function resetAcquisitionAccountBoundary() {
  // Campaign histories on a shared browser must not transfer from account A to B.
  const previous = readClientAcquisition();
  saveClientAcquisition(
    {
      version: 1,
      consent: previous.consent,
      consent_at: previous.consent_at,
      // Contact-sharing permission belongs to the person who made the choice.
      // Preserve the device's basic choice, but ask again at an account boundary.
      consent_notice_version: undefined,
      att_status: previous.att_status,
      app_surface: previous.app_surface,
      installation_id: previous.installation_id,
      meta_native_measurement: previous.meta_native_measurement,
      meta_sdk_available: previous.meta_sdk_available,
      native_os_version: previous.native_os_version,
      native_app_version: previous.native_app_version,
    },
    false,
  );
  observedUrl = "";
}
export function setupAcquisitionStorageSync(): () => void {
  const changed = (event: StorageEvent) => {
    if (event.key !== KEY || !event.newValue) return;
    try {
      const current = readClientAcquisition();
      const incoming = sanitizeAcquisition(JSON.parse(event.newValue));
      if (
        incoming.owner_user_id !== current.owner_user_id &&
        incoming.consent !== "denied"
      )
        return;
      // Consent is a browser/device choice; account histories remain separate.
      const next =
        incoming.owner_user_id === current.owner_user_id
          ? incoming
          : {
              ...current,
              consent: incoming.consent,
              consent_at: incoming.consent_at,
              consent_notice_version: incoming.consent_notice_version,
            };
      saveClientAcquisition(mergeAcquisition(current, next), false);
    } catch {
      /* Ignore invalid browser storage. */
    }
  };
  window.addEventListener("storage", changed);
  return () => window.removeEventListener("storage", changed);
}
export function setupStoreClickTracking(): () => void {
  const click = (event: MouseEvent) => {
    if (!(event.target instanceof Element)) return;
    const anchor = event.target.closest<HTMLAnchorElement>("a[href]");
    if (!anchor) return;
    try {
      const target = new URL(anchor.dataset.hiiveStoreHref ?? anchor.href);
      const store =
        target.hostname === "apps.apple.com"
          ? "ios"
          : target.hostname === "play.google.com" &&
              target.pathname === "/store/apps/details"
            ? "android"
            : null;
      if (!store) return;
      anchor.dataset.hiiveStoreHref = target.toString();
      anchor.href = target.toString();
      captureAcquisitionEvent(ANALYTICS_EVENTS.APP_STORE_LINK_CLICKED, {
        store,
        source_path: window.location.pathname,
      });
    } catch {
      /* Ignore non-URL anchors. */
    }
  };
  document.addEventListener("click", click, true);
  return () => document.removeEventListener("click", click, true);
}
